import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { colorFor } from "../lib/editorSession";

// Live collaboration for one episode in the Episode Editor (2026-09-28).
//
// One Supabase Realtime channel per episode carries:
//   • presence — who has the episode open, and which field each tab is in;
//   • field locks — derived from presence: a field is locked while another
//     tab is focused on it. Presence vanishes when a tab closes or drops, so a
//     lock can never outlive its holder; an idle holder's lock also lapses
//     after LOCK_TTL_MS so a cursor left in a box can't block the team;
//   • postgres_changes — saved rows from the episode's tables, handed to the
//     page to merge (db/episode-editor-collab.sql adds them to the realtime
//     publication).

export const LOCK_TTL_MS = 5 * 60 * 1000;
const KEEPALIVE_MS = 30 * 1000;

const tabId = Math.random().toString(36).slice(2, 10);

export default function useEpisodeCollab({ db, episodeId, me, onRowChange }) {
  const [peers, setPeers] = useState([]);
  // Focus changes also go out as broadcasts: presence updates are batched by
  // the server (~3 s to arrive), broadcasts arrive in well under a second.
  // Presence stays authoritative for who is here and clears everything a
  // departed tab held.
  const [focusByTab, setFocusByTab] = useState({});
  const [status, setStatus] = useState("connecting");
  const [, setTick] = useState(0);
  const channelRef = useRef(null);
  const focusRef = useRef(null);
  const lastSentRef = useRef(0);
  const onRowChangeRef = useRef(onRowChange);
  onRowChangeRef.current = onRowChange;

  const base = useMemo(() => (me ? { id: me.id, name: me.name, color: colorFor(me.id), tab: tabId } : null), [me]);

  const track = useCallback((field) => {
    const ch = channelRef.current;
    if (!ch || !base) return;
    const at = Date.now();
    lastSentRef.current = at;
    ch.send({ type: "broadcast", event: "focus", payload: { tab: tabId, id: base.id, field, at } }).catch(() => {});
    ch.track({ ...base, field, at }).catch(() => {});
  }, [base]);

  useEffect(() => {
    if (!db || !base || !episodeId) return undefined;
    const ch = db.channel(`episode-editor:${episodeId}`, { config: { presence: { key: `${base.id}:${tabId}` } } });

    ch.on("presence", { event: "sync" }, () => {
      const state = ch.presenceState();
      const list = Object.values(state).map((metas) => metas[metas.length - 1]).filter(Boolean);
      setPeers(list);
      // Drop fast-path focus for tabs that have left.
      const live = new Set(list.map((p) => p.tab));
      setFocusByTab((m) => Object.fromEntries(Object.entries(m).filter(([tab]) => live.has(tab))));
    });
    ch.on("broadcast", { event: "focus" }, ({ payload }) => {
      if (payload?.tab && payload.tab !== tabId) setFocusByTab((m) => ({ ...m, [payload.tab]: payload }));
    });

    const eq = `episode_id=eq.${episodeId}`;
    [["performances", eq], ["episode_storytellers", eq], ["episodes", eq], ["episode_jobs", eq],
      ["pipeline_runs", eq], ["editor_audit", eq], ["stories", null]].forEach(([table, filter]) => {
      ch.on("postgres_changes", { event: "*", schema: "public", table, ...(filter ? { filter } : {}) },
        (payload) => onRowChangeRef.current?.(table, payload));
    });

    ch.subscribe((s) => {
      setStatus(s === "SUBSCRIBED" ? "live" : s === "CHANNEL_ERROR" || s === "TIMED_OUT" ? "offline" : "connecting");
      if (s === "SUBSCRIBED") track(focusRef.current);
    });
    channelRef.current = ch;

    // Re-render periodically so idle locks expire on screen.
    const t = setInterval(() => setTick((x) => x + 1), KEEPALIVE_MS);
    return () => {
      clearInterval(t);
      channelRef.current = null;
      db.removeChannel(ch);
    };
  }, [db, episodeId, base, track]);

  const focus = useCallback((field) => { focusRef.current = field; track(field); }, [track]);
  const blur = useCallback(() => { focusRef.current = null; track(null); }, [track]);
  // Called on typing: refresh this tab's lock at most every 30 s.
  const keepAlive = useCallback(() => {
    if (focusRef.current && Date.now() - lastSentRef.current > KEEPALIVE_MS) track(focusRef.current);
  }, [track]);

  // Newest of presence vs broadcast wins for each tab's focus.
  const others = peers.filter((p) => p.tab !== tabId).map((p) => {
    const f = focusByTab[p.tab];
    return f && (f.at || 0) >= (p.at || 0) ? { ...p, field: f.field, at: f.at } : p;
  });

  const lockedBy = useCallback((field) => {
    if (!field) return null;
    const now = Date.now();
    return others.find((p) => p.field === field && now - (p.at || 0) < LOCK_TTL_MS) || null;
  }, [others]);

  // One entry per person (a person may have several tabs open).
  const people = useMemo(() => {
    const m = new Map();
    peers.forEach((p) => { if (!m.has(p.id)) m.set(p.id, { ...p, me: p.id === me?.id }); });
    return [...m.values()];
  }, [peers, me]);

  // Props for any lockable input: readOnly while someone else holds it.
  const bind = useCallback((field) => {
    const holder = lockedBy(field);
    return {
      holder,
      readOnly: !!holder,
      onFocus: () => focus(field),
      onBlur: () => blur(),
      onInput: keepAlive,
    };
  }, [lockedBy, focus, blur, keepAlive]);

  return { status, people, lockedBy, focus, blur, keepAlive, bind, focusRef };
}
